import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import { eq } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsAlert,
  HdsFormRadioCardGroup,
  HdsBadge,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
} from 'core/components';
import { boxPadding, boxMargin } from 'core/modifiers';

export default class PageOrganizationsDetailSSOSelectMethod extends Component {
  @service api;
  @service router;

  // set default
  @tracked selectedProtocol = this.protocols.OIDC.name;

  get protocols() {
    return {
      OIDC: {
        name: 'oidc',
        img: '/svgs/open-id.svg',
      },
      SAML: {
        name: 'saml',
        img: '/svgs/saml.svg',
      },
    };
  }

  onProtocolChange = (e) => {
    this.selectedProtocol = e.target.value;
  };

  onClick = () => {
    if (this.selectedProtocol === this.protocols.OIDC.name) {
      this.router.transitionTo('cloud.orgs.detail.settings.sso.oidc.configure');
    } else {
      // redirect to saml form
      this.router.transitionTo('cloud.orgs.detail.settings.sso.saml.configure');
    }
  };

  onCancel = () => {
    // redirect to SSO settings page
    this.router.transitionTo('cloud.orgs.detail.settings.sso');
  };

  <template>
    <div data-test-sso-select-method>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t
                "components.page.organizations.sso.select-method.protocol.title"
              }}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          {{#if (variation "hcp-identity-scim-enabled")}}
            <HdsAlert
              @type="inline"
              @color="highlight"
              data-test-scim-private-beta-banner
              as |A|
            >
              <A.Title>
                {{t
                  "components.page.organizations.sso.scim-private-beta-banner.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.organizations.sso.scim-private-beta-banner.content"
                  htmlSafe=true
                }}
              </A.Description>
            </HdsAlert>
          {{/if}}
        </:alert>
        <:body>
          <HdsFormRadioCardGroup
            @alignment="center"
            {{boxPadding "0 0 xl 0"}}
            as |G|
          >
            <G.Legend>
              {{t
                "components.page.organizations.sso.select-method.protocol.description"
              }}
            </G.Legend>

            <G.RadioCard
              data-test-oidc-radio-card
              id="sso-select-method_protocol-card__oidc"
              @checked={{eq this.selectedProtocol this.protocols.OIDC.name}}
              @maxWidth="25%"
              @value={{this.protocols.OIDC.name}}
              {{on "change" this.onProtocolChange}}
              as |R|
            >
              <R.Generic>
                <img
                  width="30"
                  src={{this.protocols.OIDC.img}}
                  alt={{t
                    "components.page.organizations.sso.select-method.protocol.oidc"
                  }}
                />
                <p class="hds-typography-display-300 hds-font-weight-bold">
                  {{t
                    "components.page.organizations.sso.select-method.protocol.oidc"
                  }}
                </p>
              </R.Generic>
            </G.RadioCard>

            <G.RadioCard
              data-test-saml-radio-card
              id="sso-select-method_protocol-card__saml"
              @checked={{eq this.selectedProtocol this.protocols.SAML.name}}
              @maxWidth="25%"
              @value={{this.protocols.SAML.name}}
              {{on "change" this.onProtocolChange}}
              as |R|
            >
              <R.Generic>
                <img
                  width="30"
                  src={{this.protocols.SAML.img}}
                  alt={{t
                    "components.page.organizations.sso.select-method.protocol.saml"
                  }}
                />
                <p class="hds-typography-display-300 hds-font-weight-bold">
                  {{t
                    "components.page.organizations.sso.select-method.protocol.saml"
                  }}
                </p>
                {{#if (variation "hcp-identity-scim-enabled")}}
                  <HdsBadge
                    @text={{t
                      "components.page.organizations.sso.select-method.protocol.badge"
                    }}
                    @color="neutral"
                    @size="small"
                    data-test-scim-compatible-badge
                    {{boxMargin "xs 0 0 0"}}
                  />
                {{/if}}
              </R.Generic>
            </G.RadioCard>
          </HdsFormRadioCardGroup>

          <FlexGrid as |G|>
            <G.Item @xs="12">
              <HdsButtonSet>
                <HdsButton
                  @color="primary"
                  @text={{t
                    "components.page.organizations.sso.select-method.actions.next"
                  }}
                  data-test-configure-button
                  {{on "click" this.onClick}}
                />
                <HdsButton
                  @color="secondary"
                  @text={{t
                    "components.page.organizations.sso.select-method.actions.cancel"
                  }}
                  data-test-cancel-button
                  {{on "click" this.onCancel}}
                />
              </HdsButtonSet>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </div>
  </template>
}
